import type { ScentProfile } from '../../../../packages/shared/src/index.js';
const empty: ScentProfile = {
  accords: [],
  projection: null,
  longevity: null,
  basis: '',
  sources: [],
  reviewedAt: null,
};
export function ScentProfileEditor({
  value,
  onChange,
}: {
  value?: ScentProfile | null;
  onChange: (profile: ScentProfile | null) => void;
}) {
  const profile = value ?? empty;
  const set = (key: keyof ScentProfile, next: unknown) => onChange({ ...profile, [key]: next });
  return (
    <div className="profile-editor">
      <p className="field-help">
        Choose up to 6 supported accords. Rate each accord from fragrance-specific evidence before
        publishing. Scores describe editorial emphasis from 0 (absent) to 5 (defining), never
        measured similarity. Unlisted accords count as 0 when comparing recorded profiles. Leave
        an accord’s rating blank if it is not yet assessed.
      </p>
      <div className="accord-editor-list">
        {profile.accords.map((accord, i) => (
          <div className="accord-editor-row" key={i}>
            <label>
              Accord {i + 1}
              <input
                value={accord.name}
                maxLength={40}
                placeholder="e.g. Warm Spicy"
                onChange={(e) =>
                  set(
                    'accords',
                    profile.accords.map((a, j) => (j === i ? { ...a, name: e.target.value } : a)),
                  )
                }
              />
            </label>
            <label>
              Intensity
              <select
                required
                value={accord.intensity ?? ''}
                onChange={(e) =>
                  set(
                    'accords',
                    profile.accords.map((a, j) =>
                      j === i
                        ? { ...a, intensity: e.target.value ? Number(e.target.value) : null }
                        : a,
                    ),
                  )
                }
              >
                <option value="" disabled>
                  Choose a rating
                </option>
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n === 0 ? '0 / 5 · Absent' : n + ' / 5'}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="text-link"
              aria-label={'Remove accord ' + (accord.name || i + 1)}
              onClick={() =>
                set(
                  'accords',
                  profile.accords.filter((_, j) => i !== j),
                )
              }
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      {profile.accords.length < 6 && (
        <button
          type="button"
          className="button secondary"
          onClick={() => set('accords', [...profile.accords, { name: '', intensity: null }])}
        >
          Add accord
        </button>
      )}
      <div className="performance-editor-fields">
        <label>
          Expected projection
          <select
            value={profile.projection ?? ''}
            onChange={(e) => set('projection', e.target.value || null)}
          >
            <option value="">Not recorded</option>
            {['Soft', 'Moderate', 'Strong'].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          Expected longevity
          <select
            value={profile.longevity ?? ''}
            onChange={(e) => set('longevity', e.target.value || null)}
          >
            <option value="">Not recorded</option>
            {['Light', 'Moderate', 'Long-lasting', 'Very long-lasting'].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
      </div>
      <label>
        Wear notes
        <textarea
          rows={3}
          maxLength={800}
          value={profile.performanceNote ?? ''}
          onChange={(e) => set('performanceNote', e.target.value)}
          placeholder="Summarise wear reports and any uncertainty for this version."
        />
      </label>
      <label>
        Estimate basis
        <textarea
          rows={4}
          value={profile.basis}
          onChange={(e) => set('basis', e.target.value)}
          placeholder="Explain the published descriptions or community reports used. Include any uncertainty."
        />
      </label>
      <label>
        Estimate source URLs
        <textarea
          rows={3}
          value={profile.sources.join('\n')}
          onChange={(e) => set('sources', e.target.value.split('\n'))}
          placeholder="One URL per line"
        />
      </label>
      <label>
        Estimates reviewed on
        <input
          type="date"
          value={profile.reviewedAt?.slice(0, 10) ?? ''}
          onChange={(e) =>
            set('reviewedAt', e.target.value ? e.target.value + 'T00:00:00.000Z' : null)
          }
        />
      </label>
      {value && (
        <button type="button" className="text-link" onClick={() => onChange(null)}>
          Clear all estimates
        </button>
      )}
    </div>
  );
}
